<template>
  <b-container
    class="square-transaction-card-payment-wrapper"
  >
    <square-payments-response-modals
      :success-component="successComponent"
      :failure-component="failureComponent"
      :error-component="errorComponent"
      :debug="false"
      @closed="completeModalClose"
    />
    <b-row
      class="mt-3"
    />
    <b-row
      v-show="!processingSuccess"
      class="mt-3"
    >
      <b-col>
        <div id="square-card-container" />
      </b-col>
    </b-row>
    <b-row v-show="showStoreCardButton && !processingSuccess">
      <b-col>
        <payment-button
          id="store-card-button"
          data-cy="store-card-button"
          :disabled="processing || processingSuccess"
          form-type="StoreCreditCard"
          :label="label"
          @click="storeCardDetails"
        >
          Store Card Details
        </payment-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SquarePaymentsMixin from '../mixins/square-payments'
import SquarePaymentResponseModals from '../modals/response-modals.vue'

import PaymentButton from '../cards/card-payment-button.vue'

export default {
  name: 'square-payments-store-card',
  components: {
    'square-payments-response-modals': SquarePaymentResponseModals,
    'payment-button': PaymentButton
  },
  mixins: [
    SquarePaymentsMixin
  ],
  props: {
    account: {
      type: Object,
      required: true
    },
    recaptchaError: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    successComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    failureComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    errorComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        removeStoreCardButton: false,
        defaultCardholderName: '',
        overrideSource: 'default',
        requestOptions: {}
      })
    }
  },
  data: function() {
    return {
      submitButtonVisible: false,
      retryableError: false,
      cardholder_name: Object.prototype.hasOwnProperty.call(this.options, 'defaultCardholderName') ? this.options.defaultCardholderName : ''
    }
  },
  computed: {
    processingSuccess: function() {
      const result = this.$store.getters['square/processingResult']
      return !!(result && result.status && result.type === 'card')
    },
    label: function() {
      return Object.prototype.hasOwnProperty.call(this.options, 'label') ? this.options.label : 'Store Card Details'
    },
    showStoreCardButton: function() {
      const _showButtonOption = Object.prototype.hasOwnProperty.call(this.options, 'removeStoreCardButton') ? !this.options.removeStoreCardButton : true
      return _showButtonOption && this.submitButtonVisible
    },
    userid: function() {
      // Use the userid from the account if available, otherwise use the prop
      return this.account.userid
    },
    verificationDetails: function() {
      // Get the phone number from either mobile or phone, whichever is available
      const phoneNumber = (this.account.mobile && this.account.mobile.global && this.account.mobile.global.trim()) ||
                (this.account.phone && this.account.phone.global && this.account.phone.global.trim()) ||
                null

      // Process addresses to get street1 and street2 as an array
      let selectedAddress = null

      // Check if addresses array exists
      if (this.account.addresses && this.account.addresses.length > 0) {
        // Find billing address first
        selectedAddress = this.account.addresses.find(addr => addr.type === 'billing')

        // If no billing address, look for shipping
        if (!selectedAddress) {
          selectedAddress = this.account.addresses.find(addr => addr.type === 'shipping')
        }

        // If no shipping, look for delivery
        if (!selectedAddress) {
          selectedAddress = this.account.addresses.find(addr => addr.type === 'delivery')
        }

        // If none of the specific types found, use the first address
        if (!selectedAddress) {
          selectedAddress = this.account.addresses[0]
        }
      }

      // Get address lines as array, filtering out empty values
      const addressLines = []
      if (selectedAddress) {
        if (selectedAddress.street1 && selectedAddress.street1.trim()) {
          addressLines.push(selectedAddress.street1.trim())
        }
        if (selectedAddress.street2 && selectedAddress.street2.trim()) {
          addressLines.push(selectedAddress.street2.trim())
        }
      }

      return {
        amount: '1.00',
        billingContact: {
          givenName: this.account.firstname,
          familyName: this.account.lastname,
          email: this.account.email,
          phone: phoneNumber,
          addressLines: addressLines,
          city: selectedAddress ? selectedAddress.city : '',
          state: selectedAddress ? selectedAddress.state : '',
          countryCode: selectedAddress ? selectedAddress.country_code.toUpperCase() : '',
          postalCode: selectedAddress ? selectedAddress.postcode : ''
        },
        currencyCode: this.$store.getters['square/currencyCode'],
        intent: 'CHARGE_AND_STORE',
        customerInitiated: true,
        sellerKeyedIn: false
      }
    }
  },
  mounted() {
    this.loading = true
    if (this.options.defaultCardholderName !== undefined) {
      this.cardholder_name = this.options.defaultCardholderName
    }

    // Wait for the card container to become visible before initializing payment form
    this.waitForCardContainer()
  },
  beforeDestroy() {
    // Clean up intersection observer if it exists
    if (this.cardContainerObserver) {
      this.cardContainerObserver.disconnect()
      this.cardContainerObserver = null
    }

    // Clean up Square form when component is destroyed
    this.cleanupSquareForm()
  },
  methods: {
    waitForCardContainer() {
      const cardContainer = document.querySelector('#square-card-container')

      if (cardContainer && this.isElementVisible(cardContainer)) {
        // Element is already visible, initialize immediately
        this.initializePaymentFormWhenReady()
      }
      else {
        // Use Intersection Observer to wait for element to become visible
        this.cardContainerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.target.id === 'square-card-container') {
              // Element is now visible, initialize payment form
              this.initializePaymentFormWhenReady()
              // Disconnect observer since we only need to detect visibility once
              this.cardContainerObserver.disconnect()
              this.cardContainerObserver = null
            }
          })
        }, {
          // Trigger when element becomes visible (even partially)
          threshold: 0.1,
          // Use document as root to detect visibility anywhere on page
          root: null
        })

        // Start observing - use a small delay to ensure DOM is fully updated
        this.$nextTick(() => {
          const container = document.querySelector('#square-card-container')
          if (container) {
            this.cardContainerObserver.observe(container)
          }
          else {
            // If container still doesn't exist, retry after a short delay
            setTimeout(() => {
              const retryContainer = document.querySelector('#square-card-container')
              if (retryContainer) {
                this.cardContainerObserver.observe(retryContainer)
              }
              else {
                // Fallback: try to initialize anyway (original behavior)
                this.initializePaymentFormWhenReady()
              }
            }, 100)
          }
        })
      }
    },

    isElementVisible(element) {
      if (!element) return false
      const rect = element.getBoundingClientRect()
      const style = window.getComputedStyle(element)
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0'
      )
    },

    initializePaymentFormWhenReady() {
      this.loadSquarePayments()
        .then(_ => this.initializePaymentForm('#square-card-container', {
          cardholderName: this.cardholder_name,
          overrideSource: this.options.overrideSource,
          requestOptions: this.options.requestOptions
        }))
        .then(_ => {
          this.loading = false
          this.submitButtonVisible = true
        })
        .catch(err => {
          this.loading = false // Ensure loading is reset on error
          this.$emit('error', err)
        })
    },

    completeModalClose: function(modalResult) {
      if (this.retryableError) {
        this.processing = false
        this.retryableError = false
        this.$store.commit('square/PROCESSING_RESULT')
        this.$emit('error', null)
      }
      else {
        this.processing = false // Reset processing state when modal closes

        // Get the result to pass to the parent component
        const result = this.$store.getters['square/processingResult']

        // Always emit completed when modal closes, regardless of success/failure
        // The parent component will handle the result appropriately
        this.$emit('completed', result || {})
      }
    },
    storeCardDetails: async function() {
      if (this.debounceTimer !== null) {
        return
      }
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null
      }, 1500)

      this.processing = true
      this.$store.dispatch('square/storeCardDetails', {
        verificationDetails: this.verificationDetails,
        userid: this.userid,
        paymentOptions: this.requestOptions
      })
        .then(resp => {
          this.$store.commit('square/PROCESSING_RESULT', resp)
          this.processing = false // Always reset processing state
          if (resp.status) {
            this.$emit('success', resp)
            // If success modal is disabled, immediately emit completed to trigger modal close and reload
            if (this.successComponent === false) {
              this.$emit('completed', resp)
            }
          }
          else {
            this.$emit('failed', resp)
            // If failure modal is disabled, immediately emit completed
            if (this.failureComponent === false) {
              this.$emit('completed', resp)
            }
          }
        })
        .catch(err => {
          this.processing = false
          this.$store.commit('square/PROCESSING_RESULT', { status: false, type: 'card', error: err })
          this.$emit('error', err)
          // If error modal is disabled, immediately emit completed
          if (this.errorComponent === false) {
            this.$emit('completed', { status: false, type: 'card', error: err })
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
